// React i hooks
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";

// Tipovi i konstante
import { ResidualValueEditProps } from "./types";
import { Errors } from "services/forms/validation/types";
import {
  ResidualValueEditFormValues,
  ResidualValueMonth,
  ResidualValueTable,
  ResidualValueTableEditFormValues,
  ResidualValueTableValue,
} from "services/api/calculations/residualvalue/types";
import {
  initialResidualValueEditForm,
  initialResidualValueTable,
} from "services/api/calculations/residualvalue/consts";

// API pozivi
import {
  createResidualValueAPI,
  getResidualValueAPI,
  getResidualValueTableAPI,
  updateResidualValueAPI,
  updateResidualValueTableAPI,
} from "services/api/calculations/residualvalue/api";

// Validacija
import { validateYup } from "services/forms/validation/validation";
import { CALCULATION_RESIDUAL_VALUE_EDIT_FORM_SCHEMA } from "./validation";

// Pomoćni funkcionalni moduli
import { useHandleTextFieldChange } from "services/forms/hooks";
import { classNames } from "helpers";
import { transformResidualValueTableValue } from "services/api/calculations/residualvalue/transformations";

const ResidualValueEditCard: React.FC<ResidualValueEditProps> = (props) => {
  const { t } = useTranslate();
  const { residualValueId, onSuccess } = props;
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [residualValueTable, setResidualValueTable] =
    useState<ResidualValueTable>(initialResidualValueTable);

  const [formValues, setFormValues] = useState<ResidualValueEditFormValues>(
    initialResidualValueEditForm
  );

  useEffect(() => {
    if (residualValueId) {
      fetchResidualValue(residualValueId);
      fetchResidualValueTable(residualValueId);
    }
  }, [residualValueId]);

  const fetchResidualValueTable = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialResidualValueTable);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getResidualValueTableAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setResidualValueTable(result);

    setIsLoading(false);

    setErrors({});
  };

  const fetchResidualValue = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialResidualValueEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getResidualValueAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);

    setIsLoading(false);

    setErrors({});
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CALCULATION_RESIDUAL_VALUE_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      let id = !!residualValueId?.trim()
        ? residualValueId
        : !!formValues.id?.trim()
        ? formValues.id
        : null;

      if (!!id) {
        result = await updateResidualValueAPI(id, formValues);
      } else {
        result = await createResidualValueAPI(formValues);
      }

      if (result && onSuccess) {
        if (formValues.id) {
          onSuccess();
        } else {
          setFormValues(result);
          fetchResidualValueTable(result.id);
        }
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleResidualValueChange = useCallback(
    async (e: any, kmRangeId: string, monthId: string) => {
      if (formValues.id) {
        const model = {
          residualValueKmId: kmRangeId,
          residualValueMonthId: monthId,
          percentage: parseFloat(e.target.value),
        } as ResidualValueTableEditFormValues;
        await updateResidualValueTableAPI(formValues.id, model);
      }
    },
    [formValues.id]
  );

  const renderPercentageTextbox = (
    kmId: string,
    monthId: string,
    values: ResidualValueTableValue[]
  ) => {
    let valuePercentage = transformResidualValueTableValue(
      kmId,
      monthId,
      values
    );

    return (
      <TextField
        type="number"
        name="value"
        onChange={(e: any) => handleResidualValueChange(e, kmId, monthId)}
        value={valuePercentage}
        debounceTime={1000}
        required
        groupAppend="%"
        cssClassName={classNames(
          "!ring-0 !rounded-none",
          valuePercentage > 0 ? "font-bold !text-red-500" : ""
        )}
      ></TextField>
    );
  };

  return (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="space-y-3">
            <form noValidate className="text-left">
              <div className="px-5 pb-3 mb-2">
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="name"
                      label={t(
                        "admin.calculation.setting.residualvalue.add.name"
                      )}
                      placeholder={t(
                        "admin.calculation.setting.residualvalue.add.name.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.name}
                      value={formValues.name}
                      required
                      endIcon=""
                    ></TextField>
                  </div>
                </div>
              </div>
              {formValues.id && (
                <div className="px-0 pt-2 mx-[-1px] xl:px-6 mb-6">
                  <table className="table-auto border-collapse border border-slate-300 w-full">
                    <thead>
                      <tr>
                        <th className="border border-slate-300 p-2 bg-slate-100 font-bold text-center text-sm  text-slate-900 uppercase min-w-[200px]">
                          {t(
                            "admin.calculation.setting.residualvalue.add.table.header"
                          )}
                        </th>
                        {residualValueTable.kms &&
                          residualValueTable.kms.map((item, index) => (
                            <th
                              key={index}
                              className="border border-slate-300 p-2 bg-slate-50  px-4 py-3 text-xs font-normal text-center text-slate-900 uppercase"
                            >
                              {item.kms}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {residualValueTable.months &&
                        residualValueTable.months.map(
                          (monthItem: ResidualValueMonth) => (
                            <tr key={monthItem.id}>
                              <td className="border border-slate-300 p-2 bg-slate-50 text-center px-4 py-3 text-sm font-medium ">
                                {monthItem.months}
                              </td>
                              {residualValueTable.kms &&
                                residualValueTable.kms.map((kmItem, index) => (
                                  <td
                                    key={index}
                                    className="border border-slate-300  bg-white text-center  text-sm font-medium"
                                  >
                                    {renderPercentageTextbox(
                                      kmItem.id,
                                      monthItem.id,
                                      residualValueTable.values
                                    )}
                                  </td>
                                ))}
                            </tr>
                          )
                        )}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="px-5 pb-5 flex justify-end ">
                <Button
                  variant="primary"
                  size="full"
                  children={t("admin.save")}
                  onClick={handleFormSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(ResidualValueEditCard);
